import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { ProviderManifest } from '@/types/core/provider-manifest';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Add a provider from the manifest, including included features.
 */
export function useImportProviderFromManifest(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.importProviderFromManifest(tenantId),
    mutationFn: async (providerTemplate: ProviderManifest) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const providerId = await providerService.importProviderFromManifest(
        tenantId,
        providerTemplate
      );

      const provider = await pollForResource(() =>
        providerService.findProviderRaw(tenantId, providerId)
      );

      return (
        provider ?? {
          id: providerId,
          name: providerTemplate.displayName ?? providerTemplate.name,
        }
      );
    },
    onSuccess: (provider) => {
      qc.invalidateQueries({
        queryKey: providerQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.availableProviderListByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureListByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.detail(tenantId, provider.id),
        refetchType: 'all',
      });
    },
  });
}
